// =============================================================================
// Auth Store — Zustand
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  avatarUrl?: string;
}

export interface AuthCompany {
  id: string;
  name: string;
  cuit: string;
  role: string;
  permissions: string[];
  branchId?: string;
}

interface AuthState {
  user: AuthUser | null;
  companies: AuthCompany[];
  selectedCompany: AuthCompany | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  // Actions
  login: (data: {
    user: AuthUser;
    companies: AuthCompany[];
    accessToken: string;
    refreshToken: string;
  }) => void;
  logout: () => void;
  selectCompany: (company: AuthCompany) => void;
  hasPermission: (permission: string) => boolean;
  updateTokens: (accessToken: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      companies: [],
      selectedCompany: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      login: ({ user, companies, accessToken, refreshToken }) => {
        // Sync tokens to localStorage for axios interceptor
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        set({
          user,
          companies,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          // Auto-select if only one company
          selectedCompany: companies.length === 1 ? companies[0] : null,
        });

        if (companies.length === 1) {
          localStorage.setItem('selectedCompanyId', companies[0]!.id);
        }
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedCompanyId');
        set({
          user: null,
          companies: [],
          selectedCompany: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      selectCompany: (company) => {
        localStorage.setItem('selectedCompanyId', company.id);
        set({ selectedCompany: company });
      },

      hasPermission: (permission: string) => {
        const { selectedCompany } = get();
        if (!selectedCompany) return false;

        return selectedCompany.permissions.some((perm) => {
          if (perm === permission) return true;
          if (perm.endsWith('.*')) {
            const base = perm.slice(0, -2);
            return permission.startsWith(base);
          }
          return false;
        });
      },

      updateTokens: (accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        set({ accessToken, refreshToken });
      },
    }),
    {
      name: 'crm-auth',
      partialize: (state) => ({
        user: state.user,
        companies: state.companies,
        selectedCompany: state.selectedCompany,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
